import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import StoreNavbar from '@/components/feature/StoreNavbar';
import SectionLink from '@/components/feature/SectionLink';
import { requestSectionNav } from '@/lib/sectionNav';
import SafeImage from '@/components/base/SafeImage';
import { useProducts } from '@/hooks/useProducts';
import { useVariants } from '@/hooks/useVariants';
import { useCart } from '@/context/CartContext';
import { useMetaPixel } from '@/context/MetaPixelContext';
import { useCategories } from '@/hooks/useCategories';
import { useActiveDiscounts } from '@/hooks/useActiveDiscounts';
import { getProductDiscountPercent, discountedPrice } from '@/services/discounts';
import FloatingDonationDog from '@/components/FloatingDonationDog';
import FloatingWhatsapp from '@/components/FloatingWhatsapp';

function getVideoEmbed(url: string): { kind: 'youtube' | 'vimeo' | 'file'; src: string } | null {
  const clean = url.trim();
  const yt = clean.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { kind: 'youtube', src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = clean.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: 'vimeo', src: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(clean)) return { kind: 'file', src: clean };
  return null;
}

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: products, loading } = useProducts();
  const { data: variants } = useVariants();
  const { addItem } = useCart();
  const { track } = useMetaPixel();
  const { data: categories } = useCategories();
  const discounts = useActiveDiscounts();

  const product = products.find((p) => p.slug === slug);
  const cat = categories.find((c) => c.id === product?.categoryId);

  // Línea comercial: las babuchas "premium" pueden encargarse a fabricación
  // aunque no haya stock; las de línea "sencilla" solo se venden con stock real.
  const isPremium = product?.lineType === 'premium';

  // Muestra todas las tallas con variante (aunque no haya stock) para que el
  // cliente vea qué tallas existen. En línea sencilla las agotadas se bloquean.
  const sizeOptions = variants.filter((v) => v.productId === product?.id);

  const [selectedSize, setSelectedSize] = useState('');
  const [added, setAdded] = useState(false);
  const [activeMedia, setActiveMedia] = useState(0);
  const viewedRef = useRef<string | null>(null);

  // Botón "Volver": regresa al punto exacto del que venía el cliente (p. ej. su
  // posición en el catálogo). Si entró directo al enlace, va al catálogo.
  const goBack = () => {
    const idx = (window.history.state && window.history.state.idx) || 0;
    if (idx > 0) navigate(-1);
    else {
      requestSectionNav('catalogo');
      navigate('/', { state: { scrollTo: 'catalogo' } });
    }
  };

  // ViewContent: registra la vista de la ficha del producto (una vez por producto).
  useEffect(() => {
    if (!product || viewedRef.current === product.id) return;
    viewedRef.current = product.id;
    const pct = getProductDiscountPercent(product.id, discounts, product.lineType);
    const price = discountedPrice(product.commercialPrice, pct);
    track('ViewContent', {
      content_ids: [product.id],
      content_name: product.name,
      content_type: 'product',
      value: price,
      currency: 'COP',
    });
  }, [product, discounts, track]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <StoreNavbar />
        <div className="max-w-7xl mx-auto px-4 py-24 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-accent-500 text-4xl"></i>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background-50">
        <StoreNavbar />
        <div className="max-w-7xl mx-auto px-4 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300 block mb-4"></i>
          <h1 className="text-2xl font-bold text-foreground-950 mb-2">Producto no encontrado</h1>
          <p className="text-foreground-600 mb-6">No encontramos la referencia que buscas.</p>
          <SectionLink
            section="catalogo"
            className="inline-block px-6 py-3 bg-accent-500 text-white rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            Volver al catálogo
          </SectionLink>
        </div>
      </div>
    );
  }

  const discountPct = getProductDiscountPercent(product.id, discounts, product.lineType);
  const promotedPrice = discountedPrice(product.commercialPrice, discountPct);

  const media = [
    ...product.images.map((src) => ({ type: 'image' as const, src })),
    ...product.videos.map((src) => ({ type: 'video' as const, src })),
  ];
  const safeIndex = Math.min(activeMedia, Math.max(0, media.length - 1));
  const active = media[safeIndex];

  // Talla seleccionada y su disponibilidad real de inventario.
  const selectedOpt = sizeOptions.find((o) => o.sizeId === selectedSize);
  const selectedSoldOut = !!selectedOpt && selectedOpt.available <= 0;
  // En la línea sencilla/básica NO se permite agregar si la talla está agotada;
  // en la línea premium sí se permite (se marca como pedido de producción).
  const addBlocked = !selectedSize || (!isPremium && selectedSoldOut);

  return (
    <div className="relative z-0 min-h-screen bg-background-50">
      <div className="paw-watermark absolute inset-0 -z-10 pointer-events-none opacity-[0.04]" aria-hidden="true" />
      <StoreNavbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 mb-5 px-3.5 py-2 rounded-full border border-background-200 bg-white text-sm font-medium text-foreground-700 hover:border-accent-300 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          Volver
        </button>

        <nav className="text-xs text-foreground-500 mb-6 flex items-center gap-1.5 flex-wrap">
          <Link to="/" className="hover:text-accent-600 transition-colors">Inicio</Link>
          <i className="ri-arrow-right-s-line"></i>
          <SectionLink section="catalogo" className="hover:text-accent-600 transition-colors">Catálogo</SectionLink>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-700">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="aspect-square rounded-2xl overflow-hidden bg-background-100 mb-3">
              {media.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-footprint-line text-foreground-300 text-7xl"></i>
                </div>
              ) : active?.type === 'video' ? (
                <div className="w-full h-full bg-foreground-950 flex items-center justify-center">
                  {(() => {
                    const embed = getVideoEmbed(active.src);
                    if (!embed) {
                      return (
                        <a
                          href={active.src}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white text-sm underline"
                        >
                          Ver video
                        </a>
                      );
                    }
                    if (embed.kind === 'file') {
                      return <video src={embed.src} controls className="w-full h-full object-contain" />;
                    }
                    return (
                      <iframe
                        src={embed.src}
                        title="Video del producto"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    );
                  })()}
                </div>
              ) : (
                <SafeImage
                  src={active?.src ?? ''}
                  alt={product.name}
                  title={`${product.name} babuchas`}
                  className="w-full h-full object-cover object-top"
                />
              )}
            </div>

            {media.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {media.map((m, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveMedia(idx)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors cursor-pointer ${
                      idx === safeIndex ? 'border-accent-500' : 'border-transparent hover:border-background-300'
                    }`}
                  >
                    {m.type === 'image' ? (
                      <SafeImage src={m.src} alt="" className="w-full h-full object-cover object-top" />
                    ) : (
                      <div className="w-full h-full bg-foreground-950 flex items-center justify-center">
                        <i className="ri-play-circle-line text-white text-xl"></i>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            {cat && (
              <span className="text-xs font-semibold text-accent-600 uppercase tracking-wider">
                {cat.name}
              </span>
            )}
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mt-2 mb-3">
              {product.name}
            </h1>
            {(() => {
              const pct = getProductDiscountPercent(product.id, discounts, product.lineType);
              const finalPrice = discountedPrice(product.commercialPrice, pct);
              return (
                <div className="flex items-center gap-3 mb-4">
                  <p className="text-3xl font-bold text-accent-600">${finalPrice.toLocaleString()}</p>
                  {pct > 0 && (
                    <>
                      <span className="text-lg text-foreground-400 line-through">
                        ${product.commercialPrice.toLocaleString()}
                      </span>
                      <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                        -{pct}%
                      </span>
                    </>
                  )}
                </div>
              );
            })()}
            <p className="text-foreground-600 leading-relaxed mb-6">{product.description}</p>

            {product.features.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground-900 mb-3">Características</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-foreground-700">
                      <i className="ri-check-line text-accent-600"></i>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(product.sku || product.materials || product.benefits.length > 0) && (
              <div className="mb-6 pt-4 border-t border-background-100">
                <h4 className="text-sm font-semibold text-foreground-900 mb-3">Detalles</h4>
                <dl className="space-y-2 text-sm">
                  {product.sku && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-foreground-500">Referencia (SKU)</dt>
                      <dd className="text-foreground-800 font-medium">{product.sku}</dd>
                    </div>
                  )}
                  {product.materials && (
                    <div className="flex justify-between gap-4">
                      <dt className="text-foreground-500">Materiales</dt>
                      <dd className="text-foreground-800 text-right">{product.materials}</dd>
                    </div>
                  )}
                </dl>
                {product.benefits.length > 0 && (
                  <ul className="mt-3 space-y-2">
                    {product.benefits.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-foreground-700">
                        <i className="ri-star-line text-accent-600"></i>
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {sizeOptions.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-foreground-900 mb-3">Talla</h4>
                <div className="flex flex-wrap gap-2">
                  {sizeOptions.map((o) => {
                    const soldOut = o.available <= 0;
                    const isSelected = selectedSize === o.sizeId;
                    // En línea sencilla las tallas agotadas no se pueden elegir.
                    const optionDisabled = !isPremium && soldOut;
                    return (
                      <button
                        key={o.id}
                        disabled={optionDisabled}
                        onClick={() => {
                          if (optionDisabled) return;
                          setSelectedSize(o.sizeId);
                          setAdded(false);
                        }}
                        className={`px-3.5 py-2 rounded-lg border text-left transition-colors whitespace-nowrap ${
                          optionDisabled
                            ? 'bg-background-100 border-background-200 text-foreground-400 cursor-not-allowed opacity-70'
                            : isSelected
                              ? 'bg-accent-500 text-white border-accent-500 cursor-pointer'
                              : soldOut
                                ? 'bg-white border-amber-300 text-foreground-700 hover:border-amber-400 cursor-pointer'
                                : 'bg-white border-background-200 text-foreground-700 hover:border-accent-300 cursor-pointer'
                        }`}
                      >
                        <span className="block text-sm font-semibold leading-tight">
                          {o.sizeRange ? `Talla ${o.sizeRange}` : o.sizeName}
                        </span>
                        <span
                          className={`block text-[11px] mt-0.5 ${
                            optionDisabled
                              ? 'text-foreground-400'
                              : isSelected
                                ? 'opacity-90'
                                : soldOut
                                  ? 'text-amber-600'
                                  : 'text-foreground-500'
                          }`}
                        >
                          {soldOut ? (isPremium ? 'Se manda a hacer' : 'Agotado') : `${o.available} disp.`}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-foreground-500 mt-3 flex items-start gap-1.5">
                  <i className="ri-information-line mt-0.5"></i>
                  <span>
                    {isPremium
                      ? '¿Tu talla está agotada? No te preocupes: es línea Premium y la mandamos a hacer especialmente para ti.'
                      : 'Esta referencia es de línea sencilla: se vende solo con stock disponible y no se fabrica sobre pedido.'}
                  </span>
                </p>
              </div>
            )}

            <button
              onClick={() => {
                if (addBlocked || !selectedSize) return;
                addItem({
                  productId: product.id,
                  sizeId: selectedSize,
                  quantity: 1,
                  // Solo la línea premium puede ir a producción cuando no hay stock.
                  production: isPremium ? selectedSoldOut : false,
                });
                track('AddToCart', {
                  content_ids: [product.id],
                  content_name: product.name,
                  content_type: 'product',
                  value: promotedPrice,
                  currency: 'COP',
                  contents: [{ id: product.id, quantity: 1, item_price: promotedPrice }],
                });
                setAdded(true);
              }}
              disabled={addBlocked}
              className="w-full sm:w-auto px-8 py-3.5 bg-accent-500 text-white rounded-full font-semibold hover:bg-primary-500 hover:text-foreground-950 transition-all text-sm whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {!isPremium && selectedSoldOut ? (
                <>
                  <i className="ri-close-circle-line mr-2"></i>Agotado / Sin stock
                </>
              ) : added ? (
                <>
                  <i className="ri-check-line mr-2"></i>Agregado al carrito
                </>
              ) : (
                <>
                  <i className="ri-shopping-bag-3-line mr-2"></i>Agregar al carrito
                </>
              )}
            </button>

            {added && (
              <Link
                to="/checkout"
                className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap"
              >
                Ir a pagar <i className="ri-arrow-right-line"></i>
              </Link>
            )}

            {product.story && (
              <div className="mt-8 pt-6 border-t border-background-200">
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">Nuestra historia</h4>
                <p className="text-sm text-foreground-600 leading-relaxed">{product.story}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-foreground-950 text-white py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/40">
            © 2026 PCP — Paticas con Propósito. Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/pedidos" className="text-sm text-white/60 hover:text-white transition-colors">
              Mis pedidos
            </Link>
            <Link to="/admin" className="text-sm text-white/60 hover:text-white transition-colors">
              Panel Admin
            </Link>
          </div>
        </div>
      </footer>

      <FloatingDonationDog />
      <FloatingWhatsapp />
    </div>
  );
}